/*
 * COPYRIGHT © 2026 SANITÀNEXUS WHITE-LABEL. ALL RIGHTS RESERVED.
 * AUTHOR AND SOLE DEVELOPER: MARCO PANZETTI (C.F. PNZMRC89M14F251C)
 * Protected under Italian Copyright Law (Legge 633/1941) and International Treaties.
 * Unauthorized copying, reverse engineering, or distribution of this software architecture,
 * UI/UX layouts, local-first logic, or system prompt configurations is strictly prohibited.
 */
import React from 'react';
import {
  BadgeCheck, User, MapPin, Hash, Stethoscope, Clock, FileText, ExternalLink, Check, X,
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const REGISTRY_LABEL = {
  fnomceo: 'Albo FNOMCeO (Medico)',
  opi: 'OPI (Infermiere)',
  oss_qualification: 'Qualifica OSS',
};

const CATEGORY_COLOR = {
  medici: 'bg-blue-500/15 text-blue-700 dark:text-blue-300 border-blue-500/30',
  infermieri: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/30',
  oss: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/30',
};

const timeAgo = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return 'just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  } catch {
    return '';
  }
};

/**
 * A single pending-verification card used inside the Admin Onboarding queue.
 * Extracted from AdminOnboardingPanel.jsx (Phase 8 refactor).
 */
const PendingVerificationCard = ({ user, onOpenEvidence, onApprove, onReject }) => (
  <Card
    className="p-4 rounded-2xl bg-card border-border hover:shadow-md transition-shadow"
    data-testid={`admin-onboarding-row-${user.id}`}
  >
    <div className="flex items-start justify-between gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <User className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <span className="font-semibold" data-testid={`admin-onb-name-${user.id}`}>
            {user.full_name ||
              `${user.first_name || ''} ${user.last_name || ''}`.trim() ||
              user.email}
          </span>
          <span
            className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full border ${
              CATEGORY_COLOR[user.category] || 'bg-secondary text-foreground border-border'
            }`}
          >
            {user.category || '—'}
          </span>
        </div>
        <div className="text-xs text-muted-foreground truncate">{user.email}</div>

        <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1.5 text-sm">
          <div className="flex items-center gap-1.5" title="Registry type">
            <Hash className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="text-muted-foreground">Type:</span>
            <span className="font-medium truncate">
              {REGISTRY_LABEL[user.registry_type] || user.registry_type || '—'}
            </span>
          </div>
          <div className="flex items-center gap-1.5" title="Registry number">
            <BadgeCheck className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="text-muted-foreground">N°:</span>
            <span className="font-mono font-medium">{user.registry_number || '—'}</span>
          </div>
          <div className="flex items-center gap-1.5" title="Specialty">
            <Stethoscope className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="font-medium truncate">{user.specialty || '—'}</span>
          </div>
          <div className="flex items-center gap-1.5" title="Work location">
            <MapPin className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />
            <span className="font-medium truncate">{user.primary_work_location || '—'}</span>
          </div>
        </div>

        <div className="mt-2 flex items-center gap-3 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            <Clock className="h-3 w-3" aria-hidden="true" />
            Submitted {timeAgo(user.registry_verification_submitted_at)}
          </span>
          {user.cv_data_url && (
            <span className="inline-flex items-center gap-1">
              <FileText className="h-3 w-3" aria-hidden="true" /> CV attached
            </span>
          )}
        </div>
      </div>

      {user.registry_evidence_data_url && (
        <button
          type="button"
          onClick={() => onOpenEvidence(user)}
          className="w-24 h-24 rounded-lg overflow-hidden bg-secondary flex-shrink-0 group relative"
          data-testid={`admin-onb-evidence-thumb-${user.id}`}
          aria-label="Open evidence full size"
        >
          <img
            src={user.registry_evidence_data_url}
            alt={`Registry evidence for ${user.full_name || user.email}`}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 flex items-center justify-center transition-colors">
            <ExternalLink
              className="h-4 w-4 text-white opacity-0 group-hover:opacity-100"
              aria-hidden="true"
            />
          </div>
        </button>
      )}
    </div>

    <div className="mt-3 pt-3 border-t border-border flex items-center justify-end gap-2">
      {user.cv_data_url && (
        <Button variant="ghost" size="sm" asChild data-testid={`admin-onb-cv-${user.id}`}>
          <a
            href={user.cv_data_url}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1"
          >
            <FileText className="h-3.5 w-3.5" /> View CV
          </a>
        </Button>
      )}
      <Button
        variant="destructive"
        size="sm"
        onClick={() => onReject(user)}
        data-testid={`admin-onb-reject-${user.id}`}
      >
        <X className="h-3.5 w-3.5 mr-1" /> Reject
      </Button>
      <Button
        size="sm"
        onClick={() => onApprove(user)}
        data-testid={`admin-onb-approve-${user.id}`}
      >
        <Check className="h-3.5 w-3.5 mr-1" /> Approve
      </Button>
    </div>
  </Card>
);

export { REGISTRY_LABEL, CATEGORY_COLOR };
export default PendingVerificationCard;
